import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../axiosConfig';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  InputAdornment,
  Grid,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

const AddExpense = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [formData, setFormData] = useState({
    amount: '',
    description: '',
    category: 'Sundry',
    date: new Date().toISOString().split('T')[0],
    has_vat: true
  });

  // Expense categories from your system
  const expenseCategories = [
    'Accountancy & Legal',
    'Bank Charges & Interest',
    'Card Machine Fees',
    'Client Comfort',
    'Communications',
    'Electric',
    'Furniture & Equipment',
    'Gas',
    'Insurance',
    'IT & Software',
    'Leasing and Finance',
    'Licences',
    'Loan Repayment',
    'Maintenance & Repairs',
    'Marketing & Advertising',
    'Office Supplies',
    'Postage and Courier',
    'Rates',
    'Rent',
    'Stock',
    'Subscriptions',
    'Sundry',
    'Taxation',
    'Training',
    'Waste Disposal',
    'Water'
  ];

  // Categories that might be vehicle-related
  const vehicleRelatedCategories = ['Maintenance & Repairs', 'Leasing and Finance', 'Insurance'];

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError(null);
    setSuccess(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post('/solo/expenses', formData);
      
      setSuccess(`Expense added successfully! VAT: £${response.data.vat}, Net: £${response.data.net}`);
      
      // Reset form
      setFormData({
        amount: '',
        description: '',
        category: 'Sundry',
        date: new Date().toISOString().split('T')[0],
        has_vat: true
      });

      // Navigate back after 2 seconds
      setTimeout(() => {
        navigate('/solo-dashboard');
      }, 2000);

    } catch (err) {
      console.error('Error adding expense:', err);
      setError(err.response?.data?.error || 'Failed to add expense. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/solo-dashboard')}
        sx={{ mb: 3 }}
      >
        Back to Dashboard
      </Button>

      <Paper sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 700 }}>
          Add Expense
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Track your deductible business costs
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {/* Vehicle Expense Warning - shows when vehicle-related category selected */}
        {vehicleRelatedCategories.includes(formData.category) && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              ⚠️ VEHICLE COSTS WARNING
            </Typography>
            <Typography variant="caption">
              If you use the <strong>Mileage Log</strong> (45p/mile simplified allowance), DO NOT also claim vehicle expenses such as:
              <br />
              • Fuel • Insurance • Repairs • Servicing • MOT • Road Tax • Depreciation • Finance Payments
              <br /><br />
              The 45p/mile rate already covers <strong>ALL vehicle running costs</strong>. Claiming both is double-counting and HMRC will reject your return.
              <br /><br />
              <strong>Only claim actual vehicle costs if you're NOT using the mileage allowance.</strong>
            </Typography>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date"
                name="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleChange}
                required
                inputProps={{ step: '0.01', min: '0.01' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {expenseCategories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                placeholder="e.g., Monthly rent for salon, Product stock from supplier"
                multiline
                rows={2}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.has_vat}
                    onChange={handleChange}
                    name="has_vat"
                  />
                }
                label="This expense includes VAT (uncheck if VAT-exempt)"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/solo-dashboard')}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Adding...' : 'Add Expense'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Tax-deductible expenses:</strong> Only record business expenses here. 
            Personal expenses should not be included. If the amount includes VAT, it will be 
            automatically extracted at 20%.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default AddExpense;